const CLIENT_ID = process.env.REACT_APP_SPOTIFY_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_SPOTIFY_CLIENT_SECRET;
const REDIRECT_URI =
  window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost"
    ? process.env.REACT_APP_SPOTIFY_REDIRECT_URI
    : `${window.location.origin}/callback`;

const SCOPES = [
  "streaming",
  "user-read-email",
  "user-read-private",
  "user-read-playback-state",
  "user-modify-playback-state",
  "user-read-currently-playing",
  "user-top-read",
  "user-read-recently-played",
].join(" ");

// Only asked for when a venue host wants to use one of their own (private) playlists
const PLAYLIST_SCOPES = "playlist-read-private playlist-read-collaborative";

// ─── SEARCH TOKEN (Client Credentials) ───────────────────────────────────────
let searchToken = null;
let searchTokenExpiry = null;

async function getSearchToken() {
  if (searchToken && searchTokenExpiry && Date.now() < searchTokenExpiry) {
    return searchToken;
  }
  const response = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: "Basic " + btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
    },
    body: "grant_type=client_credentials",
  });
  const data = await response.json();
  if (data.access_token) {
    searchToken = data.access_token;
    searchTokenExpiry = Date.now() + data.expires_in * 1000;
    return searchToken;
  }
  throw new Error("Could not get Spotify search token");
}

// ─── SEARCH ───────────────────────────────────────────────────────────────────
export async function searchSpotify(query) {
  const token = await getSearchToken();
  const response = await fetch(
    `https://api.spotify.com/v1/search?q=${encodeURIComponent(query)}&type=track&limit=8`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const data = await response.json();
  if (!data.tracks) return [];
  return data.tracks.items.map((track) => ({
    id: track.id,
    title: track.name,
    artist: track.artists.map((a) => a.name).join(", "),
    duration: msToTime(track.duration_ms),
    genre: "Spotify",
    cover: track.album.images[1]?.url || track.album.images[0]?.url,
    album: track.album.name,
    uri: track.uri,
  }));
}

// ─── AUTH (Authorization Code Flow) ──────────────────────────────────────────
export function loginWithSpotify(source = "venue", { playlists = false } = {}) {
  sessionStorage.setItem("spotify_oauth_source", source);
  const url =
    `https://accounts.spotify.com/authorize` +
    `?client_id=${CLIENT_ID}` +
    `&response_type=code` +
    `&redirect_uri=${encodeURIComponent(REDIRECT_URI)}` +
    `&scope=${encodeURIComponent(playlists ? `${SCOPES} ${PLAYLIST_SCOPES}` : SCOPES)}` +
    `&show_dialog=true`;
  window.location.href = url;
}

export function clearSpotifyToken() {
  localStorage.removeItem("spotify_token");
  localStorage.removeItem("spotify_token_expiry");
  localStorage.removeItem("spotify_refresh_token");
  localStorage.removeItem("spotify_token_scope");
}

export async function exchangeCodeForToken(code) {
  const response = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: "Basic " + btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: REDIRECT_URI,
    }),
  });
  const data = await response.json();
  if (data.access_token) {
    localStorage.setItem("spotify_token", data.access_token);
    localStorage.setItem("spotify_token_expiry", Date.now() + data.expires_in * 1000);
    if (data.refresh_token) localStorage.setItem("spotify_refresh_token", data.refresh_token);
    if (data.scope) localStorage.setItem("spotify_token_scope", data.scope);
    return data.access_token;
  }
  return null;
}

export async function refreshSpotifyToken() {
  const refreshToken = localStorage.getItem("spotify_refresh_token");
  if (!refreshToken) return null;
  const response = await fetch("https://accounts.spotify.com/api/token", {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Authorization: "Basic " + btoa(`${CLIENT_ID}:${CLIENT_SECRET}`),
    },
    body: new URLSearchParams({ grant_type: "refresh_token", refresh_token: refreshToken }),
  });
  const data = await response.json();
  if (data.access_token) {
    localStorage.setItem("spotify_token", data.access_token);
    localStorage.setItem("spotify_token_expiry", Date.now() + data.expires_in * 1000);
    if (data.scope) localStorage.setItem("spotify_token_scope", data.scope);
    return data.access_token;
  }
  return null;
}

export function getSavedSpotifyToken() {
  const token = localStorage.getItem("spotify_token");
  const expiry = localStorage.getItem("spotify_token_expiry");
  // Only discard if token is actually expired (no buffer)
  if (token && expiry && Date.now() < parseInt(expiry)) return token;
  // Return token anyway if it exists, even without expiry info
  if (token && !expiry) return token;
  return null;
}

export function getSpotifyToken() {
  return localStorage.getItem("spotify_token") || null;
}

// A token that is still valid, refreshing it if it has expired or is about to.
// The venue's player can stay open for hours, longer than one token lasts.
export async function getValidSpotifyToken() {
  const token = localStorage.getItem("spotify_token");
  const expiry = parseInt(localStorage.getItem("spotify_token_expiry") || "0", 10);
  if (token && expiry && Date.now() < expiry - 60 * 1000) return token;
  return (await refreshSpotifyToken()) || (token && !expiry ? token : null);
}

// ─── PLAYBACK ─────────────────────────────────────────────────────────────────
export function initializePlayer(token, onReady, onError, onStateChange) {
  const setupPlayer = () => {
    // Prevent duplicate players
    if (window._tappedPlayer) {
      window._tappedPlayer.disconnect();
      window._tappedPlayer = null;
    }

    const player = new window.Spotify.Player({
      name: "Tapped Player",
      getOAuthToken: async (cb) => {
        // Always get a token that hasn't expired
        const fresh = await getValidSpotifyToken();
        cb(fresh || token);
      },
      volume: 0.8,
    });

    player.addListener("ready", ({ device_id }) => {
      console.log("✅ Tapped Player ready, device:", device_id);
      window._tappedDeviceId = device_id;
      onReady(player, device_id);
    });

    player.addListener("not_ready", ({ device_id }) => {
      console.log("⚠️ Player went offline:", device_id);
    });

    player.addListener("initialization_error", ({ message }) => {
      console.error("❌ Init error:", message);
      onError(message);
    });

    player.addListener("authentication_error", ({ message }) => {
      console.error("❌ Auth error:", message);
      onError(message);
    });

    player.addListener("account_error", ({ message }) => {
      console.error("❌ Account error:", message);
      onError(message);
    });

    player.addListener("player_state_changed", (state) => {
      if (onStateChange) onStateChange(state);
    });

    player.connect().then(success => {
      console.log(success ? "✅ Player connected" : "❌ Player failed to connect");
    });

    window._tappedPlayer = player;
  };

  // Case 1: SDK already loaded and ready
  if (window.Spotify && window.Spotify.Player) {
    setupPlayer();
    return;
  }

  // Case 2: SDK script already in DOM but not ready yet
  window.onSpotifyWebPlaybackSDKReady = setupPlayer;

  // Case 3: Need to load the SDK script
  if (!document.getElementById("spotify-sdk")) {
    const script = document.createElement("script");
    script.id = "spotify-sdk";
    script.src = "https://sdk.scdn.co/spotify-player.js";
    script.async = true;
    document.body.appendChild(script);
  }
}

export async function playSong(token, deviceId, spotifyUri, positionMs = 0) {
  console.log("🎵 Attempting to play:", spotifyUri, "on device:", deviceId);

  if (!token) throw new Error("No Spotify token available");
  if (!deviceId) throw new Error("No device ID available");

  // Step 1: Transfer playback to this device
  const transferRes = await fetch("https://api.spotify.com/v1/me/player", {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ device_ids: [deviceId], play: false }),
  });

  if (!transferRes.ok && transferRes.status !== 204) {
    const transferErr = await transferRes.json().catch(() => ({}));
    console.warn("Transfer warning:", transferErr);
  }

  // Step 2: Wait for device transfer to register
  await new Promise((r) => setTimeout(r, 1000));

  // Step 3: Play the song
  const playRes = await fetch(
    `https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`,
    {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ uris: [spotifyUri], ...(positionMs > 0 ? { position_ms: Math.floor(positionMs) } : {}) }),
    }
  );

  if (!playRes.ok) {
    const err = await playRes.json().catch(() => ({ error: { message: "Unknown error" } }));
    console.error("❌ Play error:", err);
    throw new Error(err.error?.message || `Playback failed with status ${playRes.status}`);
  }

  console.log("✅ Playback started successfully");
}

// Browsers only let a page start audio after a click. Call this from a click
// handler so the venue's speakers can keep playing on their own afterwards.
export async function activatePlayer() {
  try {
    await window._tappedPlayer?.activateElement?.();
  } catch (e) {
    console.warn("Could not activate audio element:", e);
  }
}

export async function restartTrack() {
  try {
    await window._tappedPlayer?.seek?.(0);
  } catch (e) {
    console.warn("Could not restart track:", e);
  }
}

export async function pausePlayback(token, deviceId) {
  await fetch(`https://api.spotify.com/v1/me/player/pause?device_id=${deviceId}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
  });
}

export async function resumePlayback(token, deviceId) {
  await fetch(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, {
    method: "PUT",
    headers: { Authorization: `Bearer ${token}` },
  });
}

// ─── USER SPOTIFY DATA ────────────────────────────────────────────────────────
export async function getSpotifyUserProfile(token) {
  const response = await fetch("https://api.spotify.com/v1/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.json();
}

export async function getTopTracks(token, limit = 5) {
  const response = await fetch(
    `https://api.spotify.com/v1/me/top/tracks?limit=${limit}&time_range=short_term`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const data = await response.json();
  return data.items || [];
}

// ─── PLAYLISTS (venue's default music) ───────────────────────────────────────
export function hasPlaylistAccess() {
  return (localStorage.getItem("spotify_token_scope") || "").includes("playlist-read-private");
}

// Accepts a share link, a spotify:playlist: URI, or a bare playlist id
export function parsePlaylistId(input) {
  const text = (input || "").trim();
  const m =
    text.match(/open\.spotify\.com\/(?:intl-[a-z]+\/)?playlist\/([A-Za-z0-9]{22})/i) ||
    text.match(/^spotify:playlist:([A-Za-z0-9]{22})$/i) ||
    text.match(/^([A-Za-z0-9]{22})$/);
  return m ? m[1] : null;
}

async function playlistRequest(token, path) {
  const res = await fetch(`https://api.spotify.com/v1${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = new Error(
      res.status === 401 || res.status === 403 || res.status === 404
        ? "Couldn't open that playlist. If it's private, allow playlist access and try again."
        : `Spotify returned an error (${res.status})`
    );
    err.status = res.status;
    err.needsAccess = [401, 403, 404].includes(res.status) && !hasPlaylistAccess();
    throw err;
  }
  return res.json();
}

export async function getPlaylistInfo(token, id) {
  const data = await playlistRequest(token, `/playlists/${id}?fields=name`);
  return { name: data.name || "Playlist" };
}

// All playable songs in a playlist, in the same shape as a searched song
export async function getPlaylistTracks(token, id) {
  const songs = [];
  const fields = encodeURIComponent("items(item(id,uri,name,duration_ms,is_local,type,artists(name),album(name,images)),track(id,uri,name,duration_ms,is_local,type,artists(name),album(name,images))),next");
  for (let offset = 0; offset < 1000; offset += 100) {
    let data;
    try {
      // Spotify renamed the playlist "tracks" endpoint to "items" in 2026
      data = await playlistRequest(token, `/playlists/${id}/items?limit=100&offset=${offset}&fields=${fields}`);
    } catch (err) {
      if (offset === 0 && err.status === 404) {
        data = await playlistRequest(token, `/playlists/${id}/tracks?limit=100&offset=${offset}&fields=${fields}`);
      } else {
        throw err;
      }
    }
    for (const entry of data.items || []) {
      const t = entry?.item || entry?.track;
      if (!t || t.is_local || t.type !== "track" || !t.uri) continue;
      songs.push({
        id: t.id,
        title: t.name,
        artist: (t.artists || []).map((a) => a.name).join(", "),
        duration: msToTime(t.duration_ms),
        cover: t.album?.images?.[1]?.url || t.album?.images?.[0]?.url || "",
        album: t.album?.name || "",
        uri: t.uri,
      });
    }
    if (!data.next) break;
  }
  return songs;
}

// ─── VOLUME FADES (smooth hand-offs between songs) ───────────────────────────
// Only works where the browser lets the page set volume (not iPhones/iPads, where
// the change is ignored and a hand-off is a plain cut).
export const PLAYER_VOLUME = 0.8;

export async function fadeVolume(to, ms = 1500) {
  const p = window._tappedPlayer;
  if (!p) return;
  try {
    const from = await p.getVolume();
    const steps = Math.max(1, Math.round(ms / 100));
    for (let i = 1; i <= steps; i++) {
      await p.setVolume(from + ((to - from) * i) / steps);
      await new Promise((r) => setTimeout(r, ms / steps));
    }
    await p.setVolume(to);
  } catch (e) {
    console.warn("Volume fade failed:", e);
  }
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function msToTime(ms) {
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
